import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../../../../shared/services/api/axiosInstance';

const UserCorrelationMatrix = ({ teamId, appliedFilters, isLoading: parentLoading }) => {
  const [matrixData, setMatrixData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hoveredCell, setHoveredCell] = useState(null);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });

  const styles = {
    primaryBg: "#ffffff",
    secondaryBg: "#DDE2EE",
    textPrimary: "#0f172a",
    textSecondary: "#666666",
    accentColor: "#1d0c46",
    borderColor: "#DDE2EE"
  };

  // Get cell color based on correlation value (0-1 scale)
  const getCellColor = (correlation) => {
    if (correlation === null || correlation === undefined || isNaN(correlation)) {
      return 'transparent';
    }
    
    // Correlation is already normalized to 0-1
    const intensity = Math.max(0, Math.min(1, correlation));
    
    // Use accent color with varying opacity
    const r = parseInt(styles.accentColor.slice(1, 3), 16);
    const g = parseInt(styles.accentColor.slice(3, 5), 16);
    const b = parseInt(styles.accentColor.slice(5, 7), 16);
    
    // Higher correlation = darker color (opacity from 0.1 to 0.9)
    const opacity = 0.1 + (intensity * 0.8);
    
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  // Get text color based on correlation value
  const getTextColor = (correlation) => {
    if (correlation === null || correlation === undefined || isNaN(correlation)) {
      return styles.textSecondary;
    }
    const intensity = Math.max(0, Math.min(1, correlation));
    // White text for high correlation values (> 0.7)
    return intensity > 0.7 ? '#ffffff' : styles.textPrimary;
  };

  // Format correlation value for display
  const formatCorrelation = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
      return '-';
    }
    return value.toFixed(2);
  };

  // Fetch correlation matrix data
  const fetchCorrelationMatrix = useCallback(async () => {
    if (!teamId || parentLoading) {
      return;
    }

    setIsLoading(true);
    try {
      const params = { 
        team_id: teamId
      };

      // Add website filter if selected
      if (appliedFilters.websites && appliedFilters.websites.length > 0) {
        params.websites = appliedFilters.websites.join(',');
      }

      const response = await axiosInstance.get('/overview/user-correlation-matrix', { params });

      if (response.data?.success) {
        setMatrixData(response.data.data);
      } else {
        console.warn('User correlation matrix response not successful:', response.data);
        setMatrixData(null);
      }
    } catch (error) {
      console.error('Error fetching user correlation matrix:', error);
      setMatrixData(null);
    } finally {
      setIsLoading(false);
    }
  }, [teamId, appliedFilters.websites, parentLoading]);

  useEffect(() => {
    fetchCorrelationMatrix();
  }, [fetchCorrelationMatrix]);

  // Custom Tooltip Component
  const CustomTooltip = ({ field1, field2, correlation, position }) => {
    return (
      <div 
        className="fixed bg-white p-3 border shadow-lg rounded-lg z-50 pointer-events-none"
        style={{ 
          left: position.x + 10,
          top: position.y - 10,
          borderColor: styles.borderColor,
          fontFamily: 'Raleway, sans-serif',
          maxWidth: '250px'
        }}
      >
        <p className="text-sm font-medium mb-2" style={{ color: styles.textPrimary }}>
          {field1}
        </p>
        <p className="text-xs mb-1" style={{ color: styles.textSecondary }}>
          vs {field2}
        </p>
        <p className="text-xs" style={{ color: styles.textPrimary }}>
          Correlation: <span className="font-medium">{formatCorrelation(correlation)}</span>
        </p>
      </div>
    );
  };

  const handleMouseEnter = (field1, field2, correlation, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setHoverPosition({
      x: rect.left + rect.width / 2,
      y: rect.top
    });
    setHoveredCell({
      field1,
      field2,
      correlation
    });
  };

  const handleMouseLeave = () => {
    setHoveredCell(null);
  };

  if (isLoading || parentLoading) {
    return (
      <div 
        className="p-4 md:p-6"
        style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}
      >
        <div className="flex items-center justify-center h-64">
          <div className="text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
            Loading correlation matrix...
          </div>
        </div>
      </div>
    );
  }

  if (!matrixData || !matrixData.matrix || matrixData.matrix.length === 0) {
    return (
      <div 
        className="p-4 md:p-6"
        style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}
      >
        <div className="flex items-center justify-center h-64">
          <div className="text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
            No correlation data available
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="p-4 md:p-6"
      style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}
    >
      {/* Title */}
      <div className="mb-4">
        <h3 
          className="text-sm font-medium"
          style={{ color: styles.accentColor, fontFamily: 'Raleway, sans-serif' }}
        >
          User Attribute Correlation Matrix
        </h3>
      </div>

      {/* Correlation Matrix Container */}
      <div className="overflow-x-auto w-full">
        <div className="relative w-full">
          {/* Header Row - Field Labels */}
          <div className="flex mb-2">
            <div 
              className="flex-shrink-0 p-2 text-xs font-medium"
              style={{ 
                width: '150px',
                color: styles.textPrimary, 
                fontFamily: 'Raleway, sans-serif',
                borderRight: `1px solid ${styles.borderColor}`
              }}
            >
              Attribute
            </div>
            {matrixData.fields.map((field, colIndex) => (
              <div
                key={field.key}
                className="flex-1 p-2 text-xs font-medium text-center"
                style={{ 
                  color: styles.textPrimary, 
                  fontFamily: 'Raleway, sans-serif',
                  borderRight: `1px solid ${styles.borderColor}`,
                  visibility: colIndex <= matrixData.fields.length - 1 ? 'visible' : 'hidden'
                }}
                title={field.label}
              >
                {field.label}
              </div>
            ))}
          </div>

          {/* Data Rows - Only show bottom half (including diagonal) */}
          {matrixData.fields.map((field1, rowIndex) => (
            <div key={field1.key} className="flex border-b" style={{ borderColor: styles.borderColor }}>
              {/* Field Label Column */}
              <div 
                className="flex-shrink-0 p-2 text-xs"
                style={{ 
                  width: '150px',
                  color: styles.textPrimary, 
                  fontFamily: 'Raleway, sans-serif',
                  borderRight: `1px solid ${styles.borderColor}`,
                  backgroundColor: rowIndex % 2 === 0 ? 'transparent' : styles.secondaryBg + '20'
                }}
                title={field1.label}
              >
                {field1.label}
              </div>
              
              {/* Correlation Cells - Only show bottom half (rowIndex >= colIndex) */}
              {matrixData.fields.map((field2, colIndex) => {
                // Only render cells in the bottom half (including diagonal)
                if (colIndex > rowIndex) {
                  // Upper triangle - show empty/transparent cell
                  return (
                    <div
                      key={`${field1.key}-${field2.key}`}
                      className="flex-1 p-2 text-xs text-center"
                      style={{ 
                        backgroundColor: 'transparent',
                        borderRight: `1px solid ${styles.borderColor}`,
                        color: 'transparent',
                        fontFamily: 'Raleway, sans-serif'
                      }}
                    >
                      -
                    </div>
                  );
                }
                
                // Bottom half (including diagonal) - show correlation value
                const correlation = matrixData.matrix[rowIndex] ? matrixData.matrix[rowIndex][colIndex] : null;
                const cellColor = getCellColor(correlation);
                const textColor = getTextColor(correlation);
                
                return (
                  <div
                    key={`${field1.key}-${field2.key}`}
                    className="flex-1 p-2 text-xs text-center cursor-pointer transition-all duration-200 hover:scale-105"
                    style={{ 
                      backgroundColor: cellColor,
                      borderRight: `1px solid ${styles.borderColor}`,
                      color: textColor,
                      fontFamily: 'Raleway, sans-serif'
                    }}
                    onMouseEnter={(e) => handleMouseEnter(field1.label, field2.label, correlation, e)}
                    onMouseLeave={handleMouseLeave}
                  >
                    {formatCorrelation(correlation)}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Tooltip */}
      {hoveredCell && (
        <CustomTooltip 
          field1={hoveredCell.field1}
          field2={hoveredCell.field2}
          correlation={hoveredCell.correlation}
          position={hoverPosition}
        />
      )}
    </div>
  );
};

export default UserCorrelationMatrix;

